import React, {
	useState,
	useEffect,
	forwardRef,
	useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, TextareaAutosize, TextField } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import classNames from 'classnames';
import Tags from '../FormFields/Tags';
import { FIELD_TYPES, INPUT_TYPES } from './form.utils';
import CKEditorField from '../FormFields/CKEditor';
import { FormField } from '../../lib/interfaces/Form';
import FileUpload from '../FormFields/FileUplaoder';
import CKInlineEditorField from '../FormFields/CKEditorInline';
import './Form.scss';
import {
	ERRORS,
	vEmail,
	vHasSpecialCharacters,
	vHasSpecialCharactersForName,
	vLink,
	vMaxLength,
	vMinLength,
	vPassword,
	vShouldMatch,
} from './form.validations';
import ProgressButton from '../Simple/ProgressButton/ProgressButton';
import InputTextField from './InputTextField';
import SelectField from '../FormFields/SelectFields';

type FormProps = {
	fields: FormField[];
	initialValues?: any;
	saveButtonName: string;
	cancelButtonName?: string;
	isLoading?: boolean;
	saveButtonStyle?: string;
	fullWidth?: boolean;
	additionalButtonName?: string;
	onAdditionalButtonClickCallback?: (formValues: any) => void;
	onSubmit: (formValues: any) => void;
	onCancel?: () => void;
};

export type ResetFormHandler = {
	reset: () => void;
};

const Form = forwardRef<ResetFormHandler, FormProps>((props, ref) => {
	const {
		fields,
		initialValues,
		saveButtonName,
		cancelButtonName,
		isLoading,
		onSubmit,
		onCancel,
		saveButtonStyle,
		additionalButtonName,
		onAdditionalButtonClickCallback,
		fullWidth = false,
	} = props;
	const { t } = useTranslation();
	const [formValues, setFormValues] = useState<any>(null);
	const [validation, setValidation] = useState<any>({});
	const [isAdditionalButtonClicked, setIsAdditionalButtonClicked] =
		useState<boolean>(false);

	const checkAndSetInitialFormValues = (initialFormValues: any) => {
		if (!Object.keys(initialFormValues).length) {
			fields.forEach((item) => {
				// eslint-disable-next-line no-param-reassign
				initialFormValues[item.name] = item.init;
			});
		}
		setFormValues(initialFormValues);
	};

	useEffect(() => {
		const initialFormValues: any = formValues || initialValues || {};
		checkAndSetInitialFormValues(initialFormValues);
	}, [fields]);

	useImperativeHandle(ref, () => ({
		// Use case for clear form after submit. Control it from parent component via Ref
		reset() {
			setIsAdditionalButtonClicked(false);
			checkAndSetInitialFormValues({});
		},
	}));

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormValues({
			...formValues,
			[e.target.name]: e.target.value,
		});
	};

	const handleSelectChange = (value: string, name: string) => {
		setFormValues({
			...formValues,
			[name]: value,
		});
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormValues({
			...formValues,
			[e.target.name]: e.target.checked,
		});
	};

	const handleTagsChange = (
		value: string | undefined | string[],
		name: string,
	) => {
		setFormValues({
			...formValues,
			[name]: value,
		});
	};

	const handleFileUploaded = (value: File | undefined, name: string) => {
		setFormValues({
			...formValues,
			[name]: value,
		});
	};

	const handleRihEditorChange = (data: string, name: string) => {
		setFormValues({
			...formValues,
			[name]: data,
		});
	};

	const onAdditionalButtonClick = () => {
		if (onAdditionalButtonClickCallback) {
			onAdditionalButtonClickCallback(formValues);
		}
		setIsAdditionalButtonClicked(true);
	};

	const validate = () => {
		const invalidFields: any = {};
		fields.forEach((field: FormField) => {
			const value = formValues[field.name];

			if (field?.inputType === INPUT_TYPES.EMAIL && !vEmail(value)) {
				invalidFields[field.name] = t(ERRORS.INVALID, {
					fieldName: t(field.label),
				});
			}
			if (field?.inputType === INPUT_TYPES.LINK && !vLink(value) && value) {
				invalidFields[field.name] = t(ERRORS.INVALID, {
					fieldName: t(field.label),
				});
			}
			const minLength = field.validation?.minLength;
			const maxLength = field.validation?.maxLength;
			if (minLength && !vMinLength(value, minLength)) {
				invalidFields[field.name] = t(ERRORS.MIN_LENGTH, {
					number: minLength,
				});
			}
			if (
				field.validation?.forbidSpecialCharacters &&
				vHasSpecialCharacters(value)
			) {
				invalidFields[field.name] = t(ERRORS.INVALID, {
					fieldName: t(field.label),
				});
			}
			if (
				field.validation?.forbidSpecialCharactersForName &&
				vHasSpecialCharactersForName(value)
			) {
				invalidFields[field.name] = t(ERRORS.INVALID, {
					fieldName: t(field.label),
				});
			}
			if (field.inputType === INPUT_TYPES.PASSWORD) {
				const validatePW: string[] = vPassword(value, field?.validation?.password);
				if (validatePW.length) {
					const errorTranslations = validatePW.map((error: string) => t(error));
					invalidFields[field.name] = t(ERRORS.PASSWORD_INVALID, {
						errors: errorTranslations.join(', '),
					});
				}
				const confirmPasswordValidation = field?.validation?.shouldMatch?.field;
				if (
					confirmPasswordValidation &&
					!vShouldMatch(value, formValues[confirmPasswordValidation])
				) {
					invalidFields[field.name] = t(ERRORS.PASSWORD_DOESNT_MATCH);
				}
			}
			if (maxLength && !vMaxLength(value, maxLength)) {
				invalidFields[field.name] = t(ERRORS.MAX_LENGTH, {
					number: maxLength,
				});
			}
			if (field?.required) {
				if (
					(field.type === FIELD_TYPES.TEXT && !value) ||
					(field.type === FIELD_TYPES.SELECT && !value) ||
					(field.type === FIELD_TYPES.MULTISELECT && !value.length) ||
					(field.type === FIELD_TYPES.RICH_EDITOR && !value) ||
					(field.type === FIELD_TYPES.RICH_INLINE_EDITOR && !value)
				) {
					invalidFields[field.name] = t(ERRORS.REQUIRED);
				}
			}
		});
		setValidation(invalidFields);
		return !!Object.keys(invalidFields).length;
	};

	const submit = () => {
		if (validate()) return;
		onSubmit(formValues);
	};

	const renderErrorMessage = (fieldName: string) => (
		<div className="Form__field__error">{validation[fieldName]}</div>
	);

	// const submitButtonClass = classNames({
	// 'Form__save-button__full': saveButtonStyle === SUBMIT_BTN_TYPE.FULL,
	// });

	const formClass = classNames({
		'Form__full-width': fullWidth,
	});

	if (!formValues) return <div>Loading...</div>;
	return (
		<div className={formClass}>
			<form noValidate className="Form">
				{fields.map((field) => {
					let fieldRenderer;
					if (field.type === FIELD_TYPES.TEXT) {
						fieldRenderer = (
							<InputTextField
								handleChange={handleChange}
								variant="outlined"
								required={field.required}
								fullWidth
								label={field.label}
								name={field.name}
								type={field.inputType || INPUT_TYPES.TEXT}
								// defaultValue={formValues[field.name]}
								className={validation[field.name] ? 'invalid' : ''}
								fieldInputType={field.inputType}
								showOnAdditionalBtnClicked={field.showOnAdditionalBtnClicked}
								isAdditionalButtonClicked={isAdditionalButtonClicked}
								field={field}
								formValues={formValues}
								size="small"
							/>
						);
					}
					if (field.type === FIELD_TYPES.TEXTAREA) {
						fieldRenderer = (
							<TextField
								onChange={handleChange}
								variant="outlined"
								required={field.required}
								fullWidth
								label={field.label}
								name={field.name}
								size="small"
								InputProps={{
									inputComponent: TextareaAutosize,
									rows: 3,
								}}
								defaultValue={formValues[field.name]}
								className={validation[field.name] ? 'invalid' : ''}
							/>
						);
					}
					if (field.type === FIELD_TYPES.MULTISELECT) {
						fieldRenderer = (
							<Tags
								{...field}
								defaultValue={formValues[field.name]}
								onItemsChange={(val: string[] | undefined) =>
									handleTagsChange(val, field.name)
								}
								className={validation[field.name] ? 'invalid' : ''}
							/>
						);
					}
					if (field.type === FIELD_TYPES.RICH_EDITOR) {
						fieldRenderer = (
							<CKEditorField
								{...field}
								defaultValue={formValues[field.name]}
								onChange={(data: string) => {
									handleRihEditorChange(data, field.name);
								}}
							/>
						);
					}
					if (field.type === FIELD_TYPES.SELECT) {
						fieldRenderer = (
							<SelectField
								field={field}
								formValues={formValues}
								validation={validation}
								handleChange={handleSelectChange}
							/>
						);
					}
					if (field.type === FIELD_TYPES.RICH_INLINE_EDITOR) {
						fieldRenderer = (
							<CKInlineEditorField
								{...field}
								defaultValue={formValues[field.name]}
								onChange={(data: string) => {
									handleRihEditorChange(data, field.name);
								}}
							/>
						);
					}
					if (field.type === FIELD_TYPES.FILE) {
						fieldRenderer = (
							<FileUpload
								{...field}
								defaultValue={formValues[field.name]}
								onFileUploaded={(value: File | undefined) =>
									handleFileUploaded(value, field.name)
								}
							/>
						);
					}
					if (field.type === FIELD_TYPES.CHECKBOX) {
						const checkbox = (
							<Checkbox
								// {...field}
								name={field.name}
								checked={!!formValues[field.name]}
								onChange={handleCheckboxChange}
							/>
						);
						fieldRenderer = (
							<FormGroup>
								<FormControlLabel control={checkbox} label={field.label} />
							</FormGroup>
						);
					}
					return (
						<div key={field.name} className="Form__field">
							{fieldRenderer}
							{validation[field.name] && renderErrorMessage(field.name)}
						</div>
					);
				})}

				<ProgressButton
					disableElevation
					handleClick={submit}
					isLoading={isLoading}
					width={saveButtonStyle}
					text={saveButtonName}
				/>
				{cancelButtonName && onCancel && (
					<Button
						disableElevation
						variant="contained"
						color="inherit"
						onClick={onCancel}
						sx={{ marginLeft: '10px' }}
					>
						{cancelButtonName}
					</Button>
				)}
				{additionalButtonName && !isAdditionalButtonClicked && (
					<Button
						disableElevation
						variant="outlined"
						color="inherit"
						onClick={onAdditionalButtonClick}
						sx={{ marginLeft: '10px' }}
					>
						{additionalButtonName}
					</Button>
				)}
			</form>
		</div>
	);
});

Form.defaultProps = {
	initialValues: {},
};

export default Form;
